import LockResetIcon from '@mui/icons-material/LockReset';
import {
  Box,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from '@mui/utils';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  MenuItemData,
  ThreeDotsMenu,
} from '../../../components/documented/ThreeDotsMenu';
import NoData from '../../../components/NoData';
import PathCircularProgress from '../../../components/PathCircularProgress';
import SearchBar from '../../../components/SearchBar/SearchBar';
import { PathAccount } from '../../../model/account/pathAccount';
import { Role } from '../../../model/account/role';
import { NewRole } from '../../../model/admin/newRole';
import {
  changeAccountRole,
  getAllHcpAccounts,
} from '../../../redux/actions/adminActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { selectAccountId } from '../../../redux/selectors/accountSelectors';
import { selectHcpAccounts } from '../../../redux/selectors/adminSelectors';
import accountService from '../../../services/AccountService';
import {
  copyIdToClipboard,
  getComparator,
  Order,
  StyledTableCell,
  StyledTableRow,
} from '../TableComponents/TableComponents';

const TableDetailContainer = styled(Box)(() => ({
  boxSizing: 'border-box',
  height: 'fit-content',
  width: '100%',
  padding: '10px 40px 10px 20px',
}));

export interface HeadCell {
  id: keyof PathAccount;
  label: string;
  numeric: boolean;
  toolTip?: string;
}

export interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof PathAccount
  ) => void;
  order: Order;
  orderBy: string;
}

function HealthcareProfessionalAccountsTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const { t } = useTranslation(['admin', 'common']);
  const createSortHandler =
    (property: keyof PathAccount) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const headCells: readonly HeadCell[] = [
    {
      id: 'firstName',
      numeric: false,
      label: t('common:fields.firstName'),
    },
    {
      id: 'lastName',
      numeric: false,
      label: t('common:fields.lastName'),
    },
    {
      id: 'email',
      numeric: false,
      label: t('common:fields.email'),
    },
    {
      id: 'accountGuid',
      numeric: false,
      label: t('common:fields.accountId'),
    },
    {
      id: 'role',
      numeric: false,
      label: t('common:fields.role'),
    },
  ];

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align="left"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <Tooltip title={headCell.toolTip || ''}>
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? t('common:table.sortedDescending')
                      : t('common:table.sortedAscending')}
                  </Box>
                ) : null}
              </TableSortLabel>
            </Tooltip>
          </StyledTableCell>
        ))}
        <StyledTableCell align="center">
          {t('common:table.twoFactorAuthentication')}
        </StyledTableCell>
        <StyledTableCell align="left">
          {t('common:table.userActions')}
        </StyledTableCell>
      </TableRow>
    </TableHead>
  );
}

interface Props {
  isLoading: boolean;
}

export const HealthcareProfessionalAccountsComponent: React.FC<Props> = ({
  isLoading,
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const accounts = useSelector(selectHcpAccounts);
  const accountId = useSelector(selectAccountId);
  const dispatch = useAppThunkDispatch();
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof PathAccount>('lastName');
  const [searchTerm, setSearchTerm] = React.useState('');

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const filteredAccounts = React.useMemo(() => {
    try {
      // If no search term, return all accounts
      if (!searchTerm) return accounts;

      // Validate accounts array
      if (!accounts || !Array.isArray(accounts) || accounts.length === 0) {
        return [];
      }

      // Get lowercase search term
      const searchLower = String(searchTerm).toLowerCase();

      // Filter accounts with error handling
      return accounts.filter((account) => {
        try {
          if (!account) return false;

          const firstName = account.firstName
            ? account.firstName.toLowerCase()
            : '';
          const lastName = account.lastName
            ? account.lastName.toLowerCase()
            : '';
          const fullName = `${firstName} ${lastName}`;
          const email = account.email ? account.email.toLowerCase() : '';
          const accountGuid = account.accountGuid
            ? account.accountGuid.toLowerCase()
            : '';

          return (
            firstName.includes(searchLower) ||
            lastName.includes(searchLower) ||
            fullName.includes(searchLower) ||
            email.includes(searchLower) ||
            accountGuid.includes(searchLower)
          );
        } catch (err) {
          console.error('Error filtering account:', err);
          return false;
        }
      });
    } catch (err) {
      console.error('Error in filteredAccounts:', err);
      return [];
    }
  }, [accounts, searchTerm]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof PathAccount
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function handleRoleChanged(newRole: any, account: PathAccount) {
    try {
      //setIsLoading(true);
      accountService
        .switchAdmin(account.email, !newRole)
        .then((response: any) => {
          const data: NewRole = {
            role: newRole,
            accountId: account.accountGuid,
          };
          dispatch(changeAccountRole(data));
        })
        .then((response: any) => {
          //setIsLoading(false);
        });
    } catch (error: any) {
      //hmmm
    }
  }

  function handleDeleteAccount(accountGuid: string) {
    try {
      //setIsLoading(true);
      accountService
        .deleteAccount(accountGuid)
        .then((response: any) => {
          dispatch(getAllHcpAccounts());
        })
        .then((response: any) => {
          //setIsLoading(false);
        });
    } catch (error: any) {
      //hmmm
    }
  }

  function handleReset2fa(account: any) {
    if (account.isTwoFactorAuthenticationEnabled) {
      accountService.resetTwoFactor(account.accountGuid).catch((error: any) => {
        //This is not the error you are looking for;
      });
    }
  }

  const menuItems: MenuItemData[] = [
    {
      label: t('common:actions.copyGuid'),
      action: (data) => copyIdToClipboard(data),
    },
    {
      label: t('common:actions.remove'),
      action: (data) => handleDeleteAccount(data),
    },
  ];

  return (
    <TableDetailContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        <Typography
          sx={{ padding: '5px 0px 5px 0px' }}
          variant="h5"
          component="h2"
        >
          {t('hcpAccounts.title')}
        </Typography>
        {!isLoading && accounts.length > 0 && (
          <SearchBar
            placeholder={t('hcpAccounts.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        )}
      </Box>
      {!isLoading ? (
        accounts.length > 0 && filteredAccounts.length > 0 ? (
          <Paper sx={{ width: '100%', mb: 2 }}>
            <TableContainer sx={{ maxHeight: 490, minWidth: 750 }}>
              <Table stickyHeader aria-labelledby="hcp-accounts-table">
                <HealthcareProfessionalAccountsTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredAccounts
                    .slice()
                    .sort(getComparator(order, orderBy))
                    .map((account) => {
                      return (
                        <StyledTableRow
                          hover
                          tabIndex={-1}
                          key={account.accountGuid}
                        >
                          <StyledTableCell align="left">
                            {account.firstName}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {account.lastName}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {account.email}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {account.accountGuid}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Select
                              sx={{ width: '140px' }}
                              value={account.role}
                              onChange={(event) =>
                                handleRoleChanged(event.target.value, account)
                              }
                              //cant modify self or super admin account types
                              disabled={
                                account.accountGuid === accountId ||
                                account.role === Role.SuperAdmin
                              }
                            >
                              <MenuItem value={0}>
                                {t('common:roles.admin')}
                              </MenuItem>
                              <MenuItem value={1}>
                                {t('common:roles.user')}
                              </MenuItem>
                              <MenuItem disabled value={2}>
                                {t('common:roles.superAdmin')}
                              </MenuItem>
                            </Select>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Box>
                              <Tooltip title={t('hcpAccounts.reset2faTooltip')}>
                                <IconButton
                                  color="primary"
                                  size="large"
                                  onClick={() => handleReset2fa(account)}
                                >
                                  <LockResetIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            {
                              //cant delete self or super admin account type
                              account.accountGuid !== accountId &&
                              account.role !== Role.SuperAdmin ? (
                                <ThreeDotsMenu
                                  data={account.accountGuid}
                                  menuItems={menuItems}
                                />
                              ) : null
                            }
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : accounts.length > 0 && filteredAccounts.length === 0 ? (
          <NoData
            message={t('hcpAccounts.noneMatching', { term: searchTerm })}
          />
        ) : (
          <NoData message={t('hcpAccounts.empty')} />
        )
      ) : (
        <Box
          data-testid="loading-indicator" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100px', justifyContent: 'center' }}>
          <PathCircularProgress />
        </Box>
      )}
    </TableDetailContainer>
  );
};

export default HealthcareProfessionalAccountsComponent;
